import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { X, User, Shield, LogOut, UserMinus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { groupService } from '@/services/groupService'
import { useToast } from '@/contexts/ToastContext'
import { useMessagingSocket } from '@/contexts/MessagingSocketContext'
import { GroupRoom } from '@/types/group'
import { UserRole } from '@/types/user'
import {
  MessagingSocketEvent,
  IRoomMemberAddedPayload,
  IRoomMemberRemovedPayload
} from '@/types/messaging'

interface GroupInfoPanelProps {
  group: GroupRoom
  currentUserId: string
  currentUserRole?: UserRole
  isOpen: boolean
  onClose: () => void
  onLeaveGroup: () => void
  onDissolveGroup: () => void
}

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

const GroupInfoPanel: React.FC<GroupInfoPanelProps> = ({
  group,
  currentUserId,
  currentUserRole,
  isOpen,
  onClose,
  onLeaveGroup,
  onDissolveGroup
}) => {
  const { showToast } = useToast()
  const { on } = useMessagingSocket()
  const [details, setDetails] = useState<GroupRoom | null>(null)
  const [loading, setLoading] = useState(true)
  const [actingUserId, setActingUserId] = useState<string | null>(null)
  const [isConfirmingDissolve, setIsConfirmingDissolve] = useState(false)
  const [isDissolving, setIsDissolving] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false
    setLoading(true)
    groupService
      .getGroup(group.id)
      .then((data) => {
        if (!cancelled) setDetails(data)
      })
      .catch((error) => {
        showToast({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load group details'
        })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const offAdded = on<IRoomMemberAddedPayload>(MessagingSocketEvent.MEMBER_ADDED, (payload) => {
      if (payload.roomId !== group.id) return
      groupService.getGroup(group.id).then(setDetails).catch(() => undefined)
    })

    const offRemoved = on<IRoomMemberRemovedPayload>(
      MessagingSocketEvent.MEMBER_REMOVED,
      (payload) => {
        if (payload.roomId !== group.id) return
        setDetails((prev) =>
          prev
            ? {
                ...prev,
                memberIds: prev.memberIds.filter((id) => id !== payload.userId),
                adminIds: prev.adminIds.filter((id) => id !== payload.userId),
                members: prev.members?.filter((m) => m.id !== payload.userId)
              }
            : prev
        )
        if (payload.userId === currentUserId) {
          onLeaveGroup()
        }
      }
    )

    return () => {
      offAdded()
      offRemoved()
    }
  }, [group.id, isOpen, on, currentUserId, onLeaveGroup])

  if (!isOpen) return null

  const isAppAdmin = currentUserRole === UserRole.ADMIN
  const isAdmin = !!details?.adminIds.includes(currentUserId) || isAppAdmin
  const isMember = !!details?.memberIds.includes(currentUserId)
  const creator = details?.members?.find((m) => m.id === details.createdBy)

  const handleRemoveMember = async (userId: string) => {
    try {
      setActingUserId(userId)
      await groupService.removeMember(group.id, userId)
      setDetails((prev) =>
        prev
          ? {
              ...prev,
              memberIds: prev.memberIds.filter((id) => id !== userId),
              members: prev.members?.filter((m) => m.id !== userId)
            }
          : prev
      )
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to remove member'
      })
    } finally {
      setActingUserId(null)
    }
  }

  const handleLeaveGroup = async () => {
    try {
      setActingUserId(currentUserId)
      await groupService.removeMember(group.id, currentUserId)
      onLeaveGroup()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to leave group'
      })
    } finally {
      setActingUserId(null)
    }
  }

  const handleDissolveGroup = async () => {
    try {
      setIsDissolving(true)
      await groupService.dissolveGroup(group.id)
      onDissolveGroup()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to dissolve group'
      })
    } finally {
      setIsDissolving(false)
      setIsConfirmingDissolve(false)
    }
  }

  return (
    <div
      data-testid="group-info-panel"
      className="flex flex-col h-full w-full sm:w-80 border-l border-gray-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
        <h2 className="font-semibold text-gray-900">Group info</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {loading || !details ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{details.name}</h3>
            {details.description && (
              <p className="mt-1 text-sm text-gray-600">{details.description}</p>
            )}
          </div>

          <div className="text-xs text-gray-500 space-y-1">
            {creator && (
              <p>
                Created by {creator.firstName} {creator.lastName}
              </p>
            )}
            <p>Created on {formatDate(details.createdAt)}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Members ({details.members?.length ?? details.memberIds.length})
            </h4>
            <ul className="space-y-2">
              {(details.members ?? []).map((member) => {
                const memberIsAdmin = details.adminIds.includes(member.id)
                return (
                  <li
                    key={member.id}
                    data-testid="group-member-row"
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Link
                        to={`/users/${member.id}`}
                        className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        {member.profilePicture ? (
                          <img
                            src={member.profilePicture}
                            alt={member.firstName}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-4 h-4 text-gray-500" />
                        )}
                      </Link>
                      <Link to={`/users/${member.id}`} className="text-sm text-gray-900 truncate hover:underline">
                        {member.firstName} {member.lastName}
                      </Link>
                      {memberIsAdmin && (
                        <span
                          data-testid="group-member-admin-badge"
                          className="inline-flex items-center gap-0.5 text-xs text-primary-600"
                        >
                          <Shield className="w-3 h-3" />
                          Admin
                        </span>
                      )}
                    </div>
                    {(isAppAdmin || (isAdmin && !memberIsAdmin)) && (
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={actingUserId === member.id}
                        aria-label={`Remove ${member.firstName} ${member.lastName}`}
                        className="text-gray-400 hover:text-red-600 disabled:opacity-50 flex-shrink-0"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}

      <div className="border-t border-gray-200 px-4 py-4 space-y-3">
        {isMember && (
          <Button
            type="button"
            variant="danger"
            className="w-full"
            onClick={handleLeaveGroup}
            disabled={actingUserId === currentUserId}
            isLoading={actingUserId === currentUserId}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Leave Group
          </Button>
        )}

        {isAdmin &&
          (isConfirmingDissolve ? (
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-2">
                Dissolve this group? All members will be removed and this can't be undone.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsConfirmingDissolve(false)}
                  disabled={isDissolving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={handleDissolveGroup}
                  disabled={isDissolving}
                  isLoading={isDissolving}
                >
                  Yes, dissolve it
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setIsConfirmingDissolve(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Dissolve Group
            </Button>
          ))}
      </div>
    </div>
  )
}

export default GroupInfoPanel
